/**
 * Client for the public CRM intake endpoint.
 *
 * The site is static — there is no backend of its own. Submissions go to the
 * `AdminPublic` Lambda in admin.10sp.us (`auth_mode = 'none'`), which owns the
 * CRM tables. That handler resolves which site is posting from the request's
 * own Origin, so nothing here needs to prove its identity — but it does mean
 * only the allow-listed hosts can submit.
 */

import { environments as siteEnvironments, zone } from '../../site.json';
import { BUSINESS_IDENTITY } from 'store/state/useConfig';

// The intake endpoint is admin's factory public API: every factory site serves
// its API on `api.{host}`, where host is `{env}.{production host}` outside
// production. Admin's production host is admin.10sp.us.
//
// The path has two `public` segments on purpose. The first is the custom
// domain's base-path mapping for the Public API (`PublicMapping`, BasePath
// `public`, in the factory's api template); the second is the function route
// (`site.json` `public_routes`: `public` -> the Public function). The mapping
// root alone (`.../public`) is not a route and answers 403 "Missing
// Authentication Token" — the same failure fivepoint's public pages had.
const ADMIN_HOST = 'admin.10sp.us';
export const PUBLIC_ROUTE = 'public';
const adminPublicApi = (env) =>
  `https://api.${env === 'production' ? '' : `${env}.`}${ADMIN_HOST}/public/${PUBLIC_ROUTE}`;

export const ENDPOINTS = Object.freeze({
  demo: adminPublicApi('demo'),
  production: adminPublicApi('production'),
});

// The production web hosts come from this site's own factory config: the apex
// zone, its www alias, and any extra_web_hosts (`host=zoneId,...`).
const extraWebHosts = (siteEnvironments.production?.extra_web_hosts || '')
  .split(',')
  .map((entry) => entry.split('=')[0].trim().toLowerCase())
  .filter(Boolean);

export const PRODUCTION_HOSTS = Object.freeze(
  Array.from(new Set([zone, `www.${zone}`, ...extraWebHosts].map((h) => h.toLowerCase())))
);

// Anything that isn't a production hostname talks to demo, so a developer or
// a preview build can never write leads into the production CRM by accident.
export function resolveEndpoint(hostname) {
  if (import.meta.env.VITE_CONTACT_API) return import.meta.env.VITE_CONTACT_API;
  const host = (
    hostname ||
    (typeof window !== 'undefined' ? window.location.hostname : '') ||
    ''
  ).toLowerCase();
  return PRODUCTION_HOSTS.includes(host) ? ENDPOINTS.production : ENDPOINTS.demo;
}

export class ContactApiError extends Error {
  constructor(message, field) {
    super(message);
    this.name = 'ContactApiError';
    this.field = field;
  }
}

const GENERIC_FAILURE = `We couldn't send that just now. Please try again, or email ${BUSINESS_IDENTITY.contact_email}.`;

/**
 * Submit one contact form.
 *
 * Resolves with the server's message on success. Rejects with a
 * `ContactApiError` carrying the offending field name when the server rejects
 * the submission, so the form can mark the right input.
 */
export async function submitContactForm(payload, { endpoint, fetchImpl } = {}) {
  const request = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!request) throw new ContactApiError(GENERIC_FAILURE);

  let response;
  try {
    response = await request(endpoint || resolveEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'contact-submit', payload }),
    });
  } catch (error) {
    // Network failure, DNS, CORS preflight — the visitor can't act on any of
    // the detail, so it does not reach them.
    throw new ContactApiError(GENERIC_FAILURE);
  }

  let body = {};
  try {
    body = await response.json();
  } catch (error) {
    body = {};
  }

  if (!response.ok || body.ok === false) {
    throw new ContactApiError(body.message || GENERIC_FAILURE, body.field);
  }
  return body.message || 'Thanks — your message is on its way.';
}
